"use client";

import { useState, type ReactNode } from "react";
import Link from "next/link";
import { ArrowRight, Info, ThumbsDown, ThumbsUp } from "lucide-react";
import { Button } from "@/ui/components/Button";
import { Tag } from "@/ui/components/Tag";
import { Tooltip } from "@/ui/components/Tooltip";
import type { RecruitingSignal } from "@/lib/signals/types";
import { formatDate, signalType } from "@/lib/signals/presentation";
import styles from "./SignalsView.module.css";

export function SignalCard({ signal, onFeedback, pending, evidence, showSubject = false, sourcesOpen = false }: {
  signal: RecruitingSignal;
  onFeedback: (id: string, value: -1 | 1 | null) => void;
  pending?: boolean;
  /** Rendered only once the sources are opened, so closed cards cost nothing. */
  evidence: (id: string) => ReactNode;
  showSubject?: boolean;
  sourcesOpen?: boolean;
}) {
  const [open, setOpen] = useState(sourcesOpen);
  const type = signalType(signal.type);
  const points = signal.body.split("\n").map((line) => line.replace(/^•\s*/, "").trim()).filter(Boolean);
  return <article className={styles.card} aria-labelledby={`signal-${signal.id}`}>
    <header className={styles.cardHeader}>
      <div className={styles.cardMeta}>
        <Tag size="sm" color={type.tone}>{type.label}</Tag>
        {signal.isNew && <Tag size="sm" color="primary">New</Tag>}
        {type.why && <Tooltip content={type.why}>
          <button type="button" className={styles.why} aria-label={`Why this matters: ${type.why}`}><Info size={14} aria-hidden /></button>
        </Tooltip>}
        {showSubject && signal.subject.label && (signal.subject.uuid
          ? <Link className={styles.subject} href={`/candidates/${signal.subject.uuid}`}>{signal.subject.label}</Link>
          : <span className={styles.subject}>{signal.subject.label}</span>)}
        {signal.opportunity?.label && <span className={styles.muted}>{showSubject ? `at ${signal.opportunity.label}` : signal.opportunity.label}</span>}
      </div>
      {signal.occurredAt && <time className={styles.muted} dateTime={signal.occurredAt}>{formatDate(signal.occurredAt)}</time>}
    </header>
    <h3 id={`signal-${signal.id}`} className={styles.cardTitle}>{signal.title}</h3>
    {points.length > 1
      ? <ul className={styles.points}>{points.map((point, index) => <li key={index}>{point}</li>)}</ul>
      : <p className={styles.cardBody}>{points[0]}</p>}
    <p className={styles.nextStep}><ArrowRight size={14} aria-hidden /><span className={styles.srOnly}>Next step: </span>{signal.action}</p>
    {open && <div className={styles.sources}>{evidence(signal.id)}</div>}
    <footer className={styles.cardFooter}>
      <Button variant="tertiary" size="sm" aria-expanded={open} onClick={() => setOpen(!open)}>
        {open ? "Hide sources" : `${signal.evidenceCount} ${signal.evidenceCount === 1 ? "source" : "sources"}`}</Button>
      <span className={styles.footerSpacer} />
      <span className={styles.muted}>Useful?</span>
      <Button variant={signal.feedback === 1 ? "secondary" : "tertiary"} size="sm" aria-pressed={signal.feedback === 1}
        aria-label={`Useful: ${signal.title}`} disabled={pending} startIcon={<ThumbsUp size={14} aria-hidden />}
        onClick={() => onFeedback(signal.id, signal.feedback === 1 ? null : 1)}>Yes</Button>
      <Button variant="tertiary" size="sm" aria-pressed={signal.feedback === -1}
        aria-label={`Not useful: ${signal.title}`} disabled={pending} startIcon={<ThumbsDown size={14} aria-hidden />}
        onClick={() => onFeedback(signal.id, signal.feedback === -1 ? null : -1)}>{signal.feedback === -1 ? "Restore" : "No, hide it"}</Button>
    </footer>
  </article>;
}
